import React from 'react';
import { buildReferencePath } from '../../utils/url-builder';

const AUTHOR_REGEX = /^(Maxime|C\.S\.|Augustin|Thomas|Dostoïevski|Thérèse)/;

/** Matches: **Auteur** — « citation » */
const AUTHOR_QUOTE_REGEX = /^\*\*(.+?)\*\*\s*[—–]\s*«(.+)»\s*$/;

/** Renders a plain text segment with **bold** and *italic* markdown support */
export const renderBold = (text: string, keyPrefix: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={`${keyPrefix}-i${i}`}>{part.slice(1, -1)}</em>;
    }
    return <React.Fragment key={`${keyPrefix}-t${i}`}>{part}</React.Fragment>;
  });
};

/** Injecte le locale dans une URL bible4.me si elle n'en a pas déjà un. */
const rewriteBibleUrl = (url: string, locale: string): string => {
  if (/^https:\/\/bible4\.me\/[a-z]{2}\//.test(url)) return url;
  return url.replace('https://bible4.me/', `https://bible4.me/${locale}/`);
};

/**
 * Ce qui se lie dans une ligne : une adresse bible4.me écrite en toutes
 * lettres, ou une référence entre doubles parenthèses.
 *
 * Le générateur du commentaire du jour écrit les références dans la notation
 * de l'éditeur — `((Rm 13, 9))`. Rendues telles quelles elles se lisaient
 * comme une coquille, alors que ce sont les passages mêmes dont le
 * commentaire parle.
 */
const LINKABLE_REGEX = /(https:\/\/bible4\.me\/\S+|\(\([^()\n]+\)\))/g;

/**
 * Le lien d'une référence citée dans le corps du commentaire.
 *
 * Même destination que pour une note partagée (`linkifyReferences`) :
 * `/{langue}/{livre}/{chapitre}/{versets}`, le lecteur du site. Le chapeau de
 * la fiche, lui, garde son lien `/read/` — c'est le passage du jour, celui
 * qu'on veut voir s'ouvrir dans l'application.
 */
const renderReference = (raw: string, key: string | number, locale: string) => {
  const reference = raw.slice(2, -2).trim();
  const path = buildReferencePath(reference);
  // Une référence que le constructeur d'URL ne sait pas lire vaut mieux
  // affichée nue que sous ses parenthèses.
  if (!path) return <React.Fragment key={key}>{reference}</React.Fragment>;
  return (
    <a
      key={key}
      href={`/${locale}/${path}`}
      className="text-amber-600 font-semibold no-underline"
      target="_blank"
      rel="noreferrer"
    >
      {reference}
    </a>
  );
};

/** Une ligne de texte : liens, références, puis **gras** et *italique*. */
export const renderInline = (line: string, keyPrefix: string, locale: string) => {
  const parts = line.split(LINKABLE_REGEX);
  return parts.map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    if (part.startsWith('https://bible4.me/')) {
      const href = rewriteBibleUrl(part, locale);
      return (
        <a
          key={key}
          href={href}
          className="text-amber-600 font-semibold no-underline"
          target="_blank"
          rel="noreferrer"
        >
          {part}
        </a>
      );
    }
    if (part.startsWith('((') && part.endsWith('))')) {
      return renderReference(part, key, locale);
    }
    return <React.Fragment key={key}>{renderBold(part, key)}</React.Fragment>;
  });
};

interface ContentRendererProps {
  text?: string;
  locale?: string;
}

const ContentRenderer: React.FC<ContentRendererProps> = ({ text, locale = 'fr' }) => {
  const lines = (text || '').split('\n');

  return (
    <div>
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return <div key={index} className="h-2" />;
        }

        const authorQuoteMatch = trimmed.match(AUTHOR_QUOTE_REGEX);
        if (authorQuoteMatch) {
          const [, author, citation] = authorQuoteMatch;
          return (
            <blockquote
              key={index}
              className="my-2 border-l-[3px] border-amber-600 pl-3 leading-relaxed"
            >
              <span className="block text-sm font-bold text-amber-700 not-italic mb-0.5">
                {author}
              </span>
              <span className="text-base italic text-amber-800">
                «{renderInline(citation, String(index), locale)}»
              </span>
            </blockquote>
          );
        }

        if (trimmed.startsWith('«')) {
          return (
            <blockquote
              key={index}
              className="my-2 border-l-[3px] border-amber-600 pl-3 text-base italic text-amber-800 leading-relaxed"
            >
              {renderInline(trimmed, String(index), locale)}
            </blockquote>
          );
        }

        if (AUTHOR_REGEX.test(trimmed)) {
          return (
            <p
              key={index}
              className="mt-2 mb-0.5 text-sm font-bold italic text-amber-700"
            >
              {renderInline(trimmed, String(index), locale)}
            </p>
          );
        }

        if (trimmed.startsWith('❓') || trimmed.startsWith('> ')) {
          const content = trimmed.startsWith('> ') ? trimmed.slice(2) : trimmed;
          return (
            <p
              key={index}
              className="my-3 text-base font-semibold text-amber-900 leading-relaxed bg-amber-100 border border-amber-200 rounded-lg px-3 py-2"
            >
              {renderInline(content, String(index), locale)}
            </p>
          );
        }

        return (
          <p key={index} className="my-1 text-base text-amber-900/90 leading-relaxed">
            {renderInline(trimmed, String(index), locale)}
          </p>
        );
      })}
    </div>
  );
};

export default ContentRenderer;
